import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Package, CheckCircle, X, Clock, ShoppingBag, ExternalLink } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { webSocketService } from '../../services/websocket/websocket.service';
import type { NewOrderEvent, OrderStatusChangeEvent } from '../../services/websocket/websocket.service';
import { OrdersService } from '../../services/orders/orders.service';
import { OrderDetailsDrawer } from '../orders/OrderDetailsDrawer';
import { useTheme } from '../../hooks/useTheme';

export interface OrderNotification {
  id: string;
  type: 'NEW_ORDER' | 'STATUS_CHANGE';
  pedidoId: string;
  numeroPedido: string;
  clienteNombre: string;
  total: number;
  status?: string;
  timestamp: Date;
  read: boolean;
}

// Toast component
interface ToastProps {
  notification: OrderNotification;
  onClose: () => void;
  onClick: () => void;
  theme: string;
}

const Toast: React.FC<ToastProps> = ({ notification, onClose, onClick, theme }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 8000); // 8 segundos
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div 
      onClick={onClick}
      className={`fixed top-24 right-4 z-50 p-4 rounded-xl shadow-2xl border cursor-pointer transform transition-all duration-300 hover:scale-105 animate-slide-in-right ${
        theme === 'dark' 
          ? 'bg-gray-800 border-emerald-500/50' 
          : 'bg-white border-emerald-200'
      }`}
      style={{ maxWidth: '400px' }}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600 flex-shrink-0">
          <Package className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`font-semibold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              ¡Nuevo pedido recibido!
            </p>
            <span className="animate-pulse w-2 h-2 bg-red-500 rounded-full"></span>
          </div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Pedido {notification.numeroPedido}
          </p>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {notification.clienteNombre} • ${notification.total.toLocaleString()}
          </p>
          <p className={`text-xs mt-2 flex items-center gap-1 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
            <ExternalLink className="w-3 h-3" />
            Clic para ver detalles
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className={`p-1 rounded hover:bg-gray-200 ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-400'}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const OrderNotifications: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState<OrderNotification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [toast, setToast] = useState<OrderNotification | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cargar notificaciones guardadas
  useEffect(() => {
    const saved = localStorage.getItem('orderNotifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setNotifications(parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        })));
      } catch (e) {
        console.error('Error loading notifications:', e);
      }
    }
  }, []);

  // Guardar notificaciones
  useEffect(() => {
    localStorage.setItem('orderNotifications', JSON.stringify(notifications));
  }, [notifications]);

  const handleNewOrder = useCallback((event: NewOrderEvent) => {
    const notification: OrderNotification = {
      id: `${event.pedidoId}-${Date.now()}`,
      type: 'NEW_ORDER',
      pedidoId: event.pedidoId,
      numeroPedido: event.numeroPedido,
      clienteNombre: event.clienteNombre,
      total: event.total,
      timestamp: new Date(),
      read: false
    };

    addNotification(notification);

    // Mostrar toast solo si es nuevo pedido
    setToast(notification);
  }, []);

  const handleStatusChange = useCallback((event: OrderStatusChangeEvent) => {
    const notification: OrderNotification = {
      id: Date.now().toString(),
      type: 'STATUS_CHANGE',
      pedidoId: event.pedidoId,
      numeroPedido: event.numeroPedido,
      clienteNombre: '',
      total: 0,
      status: event.estado,
      timestamp: new Date(),
      read: false
    };
    
    addNotification(notification);
  }, []);

  // Deduplicación de notificaciones por pedidoId (evita toasts duplicados)
  const notifiedPedidoIds = useRef<Set<string>>(new Set());

  // Escuchar eventos de pedidos desde WebSocket
  useEffect(() => {
    const dedupedHandleNewOrder = (event: NewOrderEvent) => {
      if (notifiedPedidoIds.current.has(event.pedidoId)) return;
      notifiedPedidoIds.current.add(event.pedidoId);
      handleNewOrder(event);
    };

    webSocketService.addEventListener('new_order', dedupedHandleNewOrder);
    webSocketService.addEventListener('order_status_change', handleStatusChange);

    return () => {
      webSocketService.removeEventListener('new_order', dedupedHandleNewOrder);
      webSocketService.removeEventListener('order_status_change', handleStatusChange);
    };
  }, [handleNewOrder, handleStatusChange]);

  // Limpiar pedidoIds notificados cada 5 minutos para evitar crecimiento infinito
  useEffect(() => {
    const interval = setInterval(() => {
      notifiedPedidoIds.current.clear();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const addNotification = (notification: OrderNotification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 50));
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const openOrderDetails = async (notification: OrderNotification) => {
    markAsRead(notification.id);
    setToast(null);
    setIsOpen(false);
    
    // Si no estamos en la página de pedidos, navegar
    if (!location.pathname.includes('/dashboard/ventas')) {
      navigate('/dashboard/ventas');
    }
    
    // Cargar el pedido completo
    try {
      const response = await OrdersService.getById(notification.pedidoId);
      if (response.success) {
        setSelectedOrder(response.data);
        setIsDrawerOpen(true);
      }
    } catch (error) {
      console.error('Error cargando pedido:', error);
    }
  };

  const handleToastClick = () => {
    if (toast) {
      openOrderDetails(toast);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours} h`;
    return `Hace ${days} d`;
  };

  return (
    <>
      {/* Estilos para animación del toast */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
      {/* Toast */}
      {toast && (
        <Toast 
          notification={toast} 
          onClose={() => setToast(null)}
          onClick={handleToastClick}
          theme={theme}
        />
      )}

      <div className="relative" ref={dropdownRef}>
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className={`relative p-2 rounded-lg transition-colors ${
            theme === 'dark'
              ? 'text-gray-400 hover:text-white hover:bg-gray-800'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <Bell className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            <div className={`absolute right-0 mt-2 w-96 rounded-xl shadow-xl border z-50 overflow-hidden ${
              theme === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}>
              {/* Header */}
              <div className={`flex items-center justify-between px-4 py-3 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Notificaciones
                </h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className={`text-xs hover:underline ${
                        theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                      }`}
                    >
                      Marcar todo leído
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAll}
                      className={`text-xs hover:underline ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>

              {/* Lista */}
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className={`p-8 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No tienes notificaciones</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => openOrderDetails(notification)}
                      className={`p-4 border-b cursor-pointer transition-colors ${
                        notification.read 
                          ? theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                          : theme === 'dark' ? 'bg-emerald-900/20' : 'bg-emerald-50'
                      } ${theme === 'dark' ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          notification.type === 'NEW_ORDER'
                            ? 'bg-emerald-100 text-emerald-600'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {notification.type === 'NEW_ORDER' ? (
                            <ShoppingBag className="w-5 h-5" />
                          ) : (
                            <CheckCircle className="w-5 h-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {notification.type === 'NEW_ORDER' 
                              ? 'Nuevo pedido recibido'
                              : 'Cambio de estado'
                            }
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                            Pedido {notification.numeroPedido}
                          </p>
                          {notification.type === 'NEW_ORDER' && (
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {notification.clienteNombre} • ${notification.total.toLocaleString()}
                            </p>
                          )}
                          {notification.type === 'STATUS_CHANGE' && notification.status && (
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              Nuevo estado: {notification.status}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="w-3 h-3" />
                            <span className="text-xs">{formatTime(notification.timestamp)}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notification.id);
                          }}
                          className={`p-1 rounded hover:bg-gray-200 ${
                            theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-400'
                          }`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Drawer para ver el pedido */}
      <OrderDetailsDrawer
        isOpen={isDrawerOpen}
        pedido={selectedOrder}
        onClose={() => setIsDrawerOpen(false)}
        isAdmin={true}
        onStatusChange={() => {
          // Recargar el pedido si cambia el estado
          if (selectedOrder) {
            OrdersService.getById(selectedOrder.id).then(response => {
              if (response.success) {
                setSelectedOrder(response.data);
              }
            });
          }
        }}
      />
    </>
  );
};

export default OrderNotifications;
